import {createNamespacedHelpers} from "vuex";
import {ObjectHelper} from "~/helpers/objectHelper"

/**
 * 3 forms of inputs where 1 is only valid on +state+
 *
 * Finds value in store and makes accessible on Vue component
 * @example Basic
 * storeMapper(storeName, {
 *   state: ['formSubmission'],
 *   getters: ['initialElementValue'],
 *   mutations: ['setElementValue', 'unsetElementValue'],
 *   actions: ['autosave'],
 * })
 *
 * Finds "right" keys in store and makes usable as "left" key
 * @example Mapped
 * storeMapper(storeName, {
 *   state: ['submissionIsPersisted:isPersisted'],
 *   getters: ['initialValue:initialElementValue'],
 *   mutations: ['setValue:setElementValue', 'unsetValue:unsetElementValue'],
 *   actions: ['autosave:saveToServer'],
 * })
 *
 * Dig into hashes on state and returns deepest value
 * @example Mapped Nested (only valid on +state+)
 * storeMapper(storeName, {
 *   state: ['submissionIsPersisted:is.persisted', 'submissionIsPreview:is.preview'],
 * })
 *
 * @param storeName
 * @param state
 * @param mutations
 * @param getters
 * @param actions
 */
export default function (storeName: string, {
  state = [],
  mutations = [],
  getters = [],
  actions = [],
}: { state?: string[], mutations?: string[], getters?: string[], actions?: string[] }) {
  const { mapState, mapActions, mapGetters, mapMutations } = createNamespacedHelpers(storeName);

  return {
    computed: {
      ...mapState(parseMap(state)),
      ...mapGetters(parseMap(getters)),
    },
    methods: {
      ...mapMutations(parseMap(mutations)),
      ...mapActions(parseMap(actions)),
    },
  }
}

function parseKey(key): [string, string | ((state) => string)] {
  const [k, v] = key.split(':');
  return [k, v ? v.includes('.') ? (state) => ObjectHelper.dig(state, v) : v : k]
}

function parseMap(strings) {
  return Object.fromEntries(strings.map(parseKey))
}